import type { ServiceQueryOptions } from '@/lib/http/queryUtils';
import { moduleCheckFor, type ModuleKey } from '@/hooks/useModuleAccess';
import { chainAnalyticsService } from '@/lib/services/chain-analytics.service';
import { agendaService } from '@/lib/services/agenda.service';
import { chainService } from '@/lib/services/chain.service';
import { gettingStartedService } from '@/lib/services/getting-started.service';
import { permissionsService, type PermissionKey } from '@/lib/services/permissions.service';
import type { MeetingListParams } from '@/lib/services/meeting.service';
import { MEETING_JOIN_WINDOW_MS } from '@/lib/meeting/meeting-join-window';
import { memberPortalAgreementsService } from '@/lib/services/member-portal-agreements.service';
import { quickLinkService } from '@/lib/services/quick-link.service';
import { resourceService } from '@/lib/services/resource.service';
import { defaultOverviewRange, overviewQueryOf } from '@/pages/dashboard/chain/overview/overview-range';
import { BOOTSTRAP_QUERY_STALE_TIME } from '@/utils/reactQueryClient';
import type { QueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';

const ONE_MINUTE_MS = 60 * 1000;

/** A meeting that started this recently still counts as running, not as history. */

/** How many past meetings the history widget asks for before filtering out the still-running ones. */
const MEETING_HISTORY_LIMIT = 20;

/**
 * A clock reading floored to the whole minute, for date filters that end up in a query key.
 *
 * The route loader and the widget that mounts a few hundred milliseconds later each read their own
 * clock, and the resulting ISO string is part of the query key. Flooring is what makes the two reads
 * produce the same string, so the widget lands on the entry the loader prefetched instead of firing
 * the same request again. The cost is a loader/mount pair straddling a minute boundary: one extra
 * request, nothing worse.
 */
const flooredToMinute = (offsetMs: number): string =>
	new Date(Math.floor((Date.now() - offsetMs) / ONE_MINUTE_MS) * ONE_MINUTE_MS).toISOString();

/** The earliest start a meeting can have and still be live, since its join window is still open. */
const meetingLiveCutoff = (): string => flooredToMinute(MEETING_JOIN_WINDOW_MS);

/** Meetings that have not started yet, plus the ones whose join window is still open. */
export const upcomingMeetingsParams = (): MeetingListParams => ({
	query: {
		from: meetingLiveCutoff(),
		sortBy: 'date-asc',
	},
});

/** Everything that started up to now; `isMeetingInHistory` keeps the completed and lapsed ones. */
export const meetingHistoryParams = (): MeetingListParams => ({
	query: {
		to: flooredToMinute(0),
		sortBy: 'date-desc',
		limit: MEETING_HISTORY_LIMIT,
	},
});

/**
 * Staleness the dashboard's meeting widgets pin on top of the shared meeting-list query.
 *
 * The loader prefetches those lists a few hundred milliseconds before the widgets mount; under the
 * global `staleTime: 0` each widget would treat the just-fetched entry as stale and refetch it, so
 * the prefetch would buy a duplicate request and nothing else. Pinned here rather than on the
 * service's query definition because the meetings *page* shares that definition and should keep
 * refetching on mount. Meeting mutations invalidate this data explicitly, which ignores staleness.
 */
export const DASHBOARD_MEETING_QUERY_OPTIONS = { staleTime: BOOTSTRAP_QUERY_STALE_TIME };

const AGENDA_LOOKAHEAD_DAYS = 30;

type AgendaRangeParams = Parameters<typeof agendaService.useAgendaRange>[0];

export type DayAgendaParamsInput = {
	day: Dayjs;
};

/** Everything on the member's plate for one local day, every agenda kind. */
export const dayAgendaParams = ({ day }: DayAgendaParamsInput): AgendaRangeParams => ({
	query: { from: day.startOf('day').toISOString(), to: day.endOf('day').toISOString(), scope: 'mine' },
});

export type WeekAgendaParamsInput = {
	weekStart: Dayjs;
	days: number;
};

/** Everything on the member's plate across the day strip's week, the source of its busy-day markers. */
export const weekAgendaParams = ({ weekStart, days }: WeekAgendaParamsInput): AgendaRangeParams => ({
	query: {
		from: weekStart.startOf('day').toISOString(),
		to: weekStart
			.add(days - 1, 'day')
			.endOf('day')
			.toISOString(),
		scope: 'mine',
	},
});

/** The member's meetings from today through the next month, the upcoming-calls source. */
export const upcomingCallsParams = (): AgendaRangeParams => ({
	query: {
		from: meetingLiveCutoff(),
		to: dayjs().add(AGENDA_LOOKAHEAD_DAYS, 'day').endOf('day').toISOString(),
		kinds: ['meeting'],
		scope: 'mine',
	},
});

const INSPECTION_LOOKAHEAD_DAYS = 7;

/** The member's inspections from today through the next week, the upcoming-inspections source. */
export const upcomingInspectionsParams = (): AgendaRangeParams => ({
	query: {
		from: dayjs().startOf('day').toISOString(),
		to: dayjs().add(INSPECTION_LOOKAHEAD_DAYS, 'day').endOf('day').toISOString(),
		kinds: ['inspection'],
		scope: 'mine',
	},
});

const OVERDUE_DAYS_BACK = 30;

/** Tasks and orders dated in the last month but before today, the overdue source. */
export const overdueAgendaParams = (): AgendaRangeParams => ({
	query: {
		from: dayjs().subtract(OVERDUE_DAYS_BACK, 'day').startOf('day').toISOString(),
		to: dayjs().startOf('day').toISOString(),
		kinds: ['activity', 'order', 'follow_up'],
		scope: 'mine',
	},
});

const REVIEW_DAYS_BACK = 7;
const REVIEW_PAGE_SIZE = 50;

/** The member's own ended calls from the last week, the AI-summary review source. */
export const summariesToReviewParams = (): MeetingListParams => ({
	query: {
		from: dayjs().subtract(REVIEW_DAYS_BACK, 'day').startOf('day').toISOString(),
		to: meetingLiveCutoff(),
		ended: 'true',
		scope: 'mine',
		sortBy: 'date-desc',
		limit: REVIEW_PAGE_SIZE,
	},
});

/**
 * Everything the dashboard renders below the shell: quick links on every variant, plus the
 * getting-started state and the two agenda ranges on the non-chain variant. Each entry comes from its own service's query-options
 * builder and its params helper, so the key is literally the one the widget's hook computes.
 */
const dashboardBootQueries = (): Array<ServiceQueryOptions<unknown>> => [
	quickLinkService.useGetQuickLinks.queryOptions({}),
	gettingStartedService.useGetMyGettingStarted.queryOptions({}),
	{
		...agendaService.useAgendaRange.queryOptions(dayAgendaParams({ day: dayjs() })),
		...DASHBOARD_MEETING_QUERY_OPTIONS,
	},
	{
		...agendaService.useAgendaRange.queryOptions(upcomingCallsParams()),
		...DASHBOARD_MEETING_QUERY_OPTIONS,
	},
];

export type DashboardBootPrefetch = {
	queryClient: QueryClient;
	companyId: string;
};

/**
 * Starts the dashboard's own queries when the route matches, instead of when the widgets mount -
 * which today is after chain membership has resolved and picked a dashboard variant, a whole extra
 * request layer into the boot.
 *
 * Fire-and-forget on purpose: the loader must never delay navigation, and a request the rendered
 * variant turns out not to need just fills a cache entry nobody reads.
 */
export const prefetchDashboardBootQueries = ({ queryClient, companyId }: DashboardBootPrefetch): void => {
	if (!companyId) return;

	for (const options of dashboardBootQueries()) {
		void queryClient.prefetchQuery(options);
	}
};

/** The published-announcements slice the chain-home card shows, shared so its prefetch key matches. */
export const chainHomeAnnouncementsParams = (): Parameters<
	typeof chainService.useGetAnnouncements.queryOptions
>[0] => ({
	query: { status: 'PUBLISHED', limit: 10 },
});

export const chainHomeResourcesParams = (): Parameters<typeof resourceService.useGetResources.queryOptions>[0] => ({
	query: { page: 1, limit: 5 },
});

export const chainHomeAgreementsParams = (): Parameters<
	typeof memberPortalAgreementsService.useGetViewerAgreements.queryOptions
>[0] => ({
	query: { limit: 5, sortBy: 'discountDesc' },
});

/**
 * The chain parent home's queries that pin bootstrap staleness, over the default date range, each from
 * its own service's query-options builder so the key is the one the page hook computes.
 */
type ChainParentBootInput = {
	can: (permission: PermissionKey) => boolean;
	moduleOn: (key: ModuleKey) => boolean;
};

const chainParentBootQueries = ({ can, moduleOn }: ChainParentBootInput): Array<ServiceQueryOptions<unknown>> => {
	const query = overviewQueryOf(defaultOverviewRange());
	return [
		chainAnalyticsService.useGetOverview.queryOptions({ query }),
		...(moduleOn('courses') && can('courses.readChainLearning')
			? [chainAnalyticsService.useGetCourseAnalytics.queryOptions({ query })]
			: []),
		...(moduleOn('chain') && can('chainContent.read')
			? [chainService.useGetAnnouncements.queryOptions(chainHomeAnnouncementsParams())]
			: []),
	];
};

/**
 * The chain-parent home is a data-heavy variant behind a lazy route chunk, and which variant renders
 * is only known once aggregated counts return. This awaits those counts - already in flight from the
 * t=0 bootstrap burst, so the await is effectively a cache read - and only for a confirmed parent
 * who can read chain analytics prefetches its widgets, overlapping their data with the chunk download
 * instead of firing at mount. Everyone else falls through and their variant fetches normally on mount.
 */
export type ChainHomeBootPrefetch = DashboardBootPrefetch & {
	disabledModules: readonly string[] | undefined;
};

export const prefetchChainHomeBootQueries = async ({
	queryClient,
	companyId,
	disabledModules,
}: ChainHomeBootPrefetch): Promise<void> => {
	if (!companyId) return;
	const moduleOn = moduleCheckFor(disabledModules);
	if (!moduleOn('chain') && !moduleOn('sales')) return;

	const counts = await queryClient.fetchQuery(chainService.useGetAggregatedCounts.queryOptions({})).catch(() => null);

	if (!counts || counts.activeMembers <= 0) return;

	const effective = await queryClient
		.ensureQueryData(permissionsService.useMyEffectivePermissions.queryOptions({}))
		.catch(() => null);

	if (!effective) return;
	const can = (permission: PermissionKey) => effective.isBypass || effective.permissions.includes(permission);
	if (!can('chainHq.readAnalytics')) return;

	for (const options of chainParentBootQueries({ can, moduleOn })) {
		void queryClient.prefetchQuery(options);
	}
};
